/* eslint react/require-default-props: ["error", { "functions": "defaultArguments" }] */
import React, { cloneElement, useRef, Children } from 'react';
import styled from '@emotion/styled';
import { validateVideoInput, DurableRecording } from '@/constants';

export type FileInputFile = {
  name: string;
  size: number;
};

export type FileInputProps = {
  /** Defines acceptable file types */
  acceptedFileTypes?: Readonly<string[]>;
  /** Emotion base style */
  className?: string;
  /** The element that triggers a click on the embedded Input tag, passed as a child.
   *  Throws error if there is more than one node */
  children: React.ReactElement;
  /** Boolean indicating that the user may choose more than one file */
  multiple?: boolean;
  /** Callback for Error Handling */
  onError?: (error: Error | unknown) => void;
  /** Callback for handling file change */
  onFileChange?: (files: FileList) => void;
  /** Callback for when the button/element is clicked */
  onClick?: VoidFunction;
  /** Callback for handling file validation. Expected to throw an error when invalid */
  validate?: (file: FileInputFile) => void;
};

/**
 * FileInput component
 */
const FileInput = ({
  className = '',
  onClick = () => {},
  multiple = false,
  onError = (error) => console.error(error),
  onFileChange = () => {},
  acceptedFileTypes = DurableRecording.FILE_TYPES_SUPPORTED_FOR_UPLOAD,
  validate = (files) => validateVideoInput(files),
  children,
}: FileInputProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || !event.target.files.length) {
      return;
    }

    try {
      for (let index = 0; index < event.target.files.length; index++) {
        const file = event.target.files[index];
        validate(file);
      }
      onFileChange(event.target.files);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      onError(error);
    }
  };

  return (
    <div className={className}>
      <div className="file-input">
        {Children.only(children) &&
          cloneElement(children, {
            onClick: () => {
              if (fileInputRef.current) {
                fileInputRef.current.click();
              }
              onClick();
            },
          })}
        <input
          accept={acceptedFileTypes.join(',')}
          ref={fileInputRef}
          className="hidden-file-input"
          type="file"
          onChange={handleFileChange}
          multiple={multiple}
          data-testid="file-input"
        />
      </div>
    </div>
  );
};

const styledFileInput = styled(FileInput)`
  .file-input {
    .hidden-file-input {
      display: none;
    }
  }
`;

export default styledFileInput;
